import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useChapterEdit } from '@/module/Author/Chapter/model/ChapterQuery';
import { ChapterSchema, chapterSchema, defaultChapterValues } from '@/module/Author/Chapter/model/ChapterSchema';
import { defaultIdentifiers } from '@/module/Author/Publication/model/PublicationTypes';

import { useAppNotification } from '@feature/utils';

import { ChapterForm } from './ChapterForm';
import { Chapter } from './model/ChapterTypes';

interface ChapterEditProps {
  chapter: Chapter;
}

const convert = (chapter: Chapter): ChapterSchema => ({
  ...chapter,
  identifiers: chapter.identifiers ?? defaultIdentifiers,
});

export function ChapterEdit(props: ChapterEditProps) {
  const { chapter } = props;
  const { t } = useTranslation();
  const { mutate, isPending, isError, isSuccess } = useChapterEdit(chapter.id);
  const notification = useAppNotification();

  const onSubmit = (values: ChapterSchema) => {
    mutate(values, {
      onError: notification.error,
      onSuccess: notification.success,
    });
  };

  const values = useMemo(() => convert(chapter), [chapter]);
  const navigate = useNavigate();

  return (
    <ChapterForm
      title={t('chapters.form.title.edit')}
      schema={chapterSchema}
      defaultValues={defaultChapterValues}
      values={values}
      onSubmit={onSubmit}
      isError={isError}
      isSuccess={isSuccess}
      isSubmitting={isPending}
      onCancel={() => navigate({ to: '..' })}
      showWosFields={false}
      showScopusFields={chapter.source.type === 'SCOPUS'}
    />
  );
}
